import { SortType, SortDirection, SortState } from '../types';

/**
 * Manages sorting of recommendation cards
 */
export class SortManager {
  private sortState: SortState = {
    confidence: 'desc',
    difficulty: 'asc',
    lift: 'desc',
    quickWin: 'desc'
  };

  private activeSortType: SortType | null = 'confidence';

  /**
   * Initialize sort manager
   */
  initialize(): void {
    this.setupSortListeners();
    this.initializeSortButtons();
  }

  /**
   * Set up sort button listeners
   */
  private setupSortListeners(): void {
    const sortBar = document.getElementById('sort-bar');
    if (!sortBar) return;

    sortBar.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.sort-btn') as HTMLButtonElement;
      if (!btn) return;

      const sortType = btn.dataset.sort as SortType;

      // Toggle sort direction
      this.sortState[sortType] = this.sortState[sortType] === 'asc' ? 'desc' : 'asc';

      // Update button appearance
      this.updateSortButton(btn, sortType, this.sortState[sortType]);

      // Update active state
      document.querySelectorAll('.sort-btn').forEach((button) => {
        button.classList.remove('ring-2', 'ring-offset-2');
      });
      btn.classList.add('ring-2', 'ring-offset-2');

      // Track active sort type
      this.activeSortType = sortType;

      // Apply sort
      this.sortCards(sortType);
    });
  }

  /**
   * Initialize sort button labels
   */
  private initializeSortButtons(): void {
    document.querySelectorAll('.sort-btn').forEach((btn) => {
      const button = btn as HTMLButtonElement;
      const type = button.dataset.sort as SortType;
      this.updateSortButton(button, type, this.sortState[type]);
    });
  }

  /**
   * Update sort button label
   */
  private updateSortButton(button: HTMLButtonElement, type: SortType, direction: SortDirection): void {
    const arrows: Record<SortType, Record<SortDirection, string>> = {
      confidence: { desc: '⬇', asc: '⬆' },
      difficulty: { desc: '⬇', asc: '⬆' },
      lift: { desc: '⬇', asc: '⬆' },
      quickWin: { desc: '⬇', asc: '⬆' }
    };

    const labels: Record<SortType, string> = {
      confidence: 'Confidence',
      difficulty: 'Difficulty',
      lift: 'Expected Lift',
      quickWin: 'Quick Win'
    };

    button.innerHTML = `${labels[type]} ${arrows[type][direction]}`;
  }

  /**
   * Sort cards by type
   */
  sortCards(type: SortType): void {
    const container = document.getElementById('recommendations-container');
    if (!container) return;

    const direction = this.sortState[type];
    const sortFn = this.getSortFunction(type);

    // Get all recommendation cards (exclude empty states and button containers)
    const cards = Array.from(container.children)
      .filter((node) => {
        const element = node as HTMLElement;
        return !element.classList.contains('empty-state') &&
               !element.classList.contains('implementation-request-button-container') &&
               !element.classList.contains('header-implementation-request-button-container');
      })
      .sort((a, b) => sortFn(a as HTMLElement, b as HTMLElement, direction));

    // Re-append sorted cards
    cards.forEach((card) => container.appendChild(card));

    // Move empty state cards to the end
    Array.from(container.children)
      .filter((node) => (node as HTMLElement).classList.contains('empty-state'))
      .forEach((card) => container.appendChild(card));
  }

  /**
   * Get sort function for a specific type
   */
  private getSortFunction(type: SortType): (a: HTMLElement, b: HTMLElement, direction: SortDirection) => number {
    const sortFns: Record<
      SortType,
      (a: HTMLElement, b: HTMLElement, direction: SortDirection) => number
    > = {
      confidence: (a, b, direction) => {
        const mult = direction === 'desc' ? 1 : -1;
        const aRank = parseInt(a.dataset.confidenceRank || '0');
        const bRank = parseInt(b.dataset.confidenceRank || '0');

        if (bRank !== aRank) {
          return mult * (bRank - aRank);
        }

        const aEffort = parseInt(a.dataset.effort || '3');
        const bEffort = parseInt(b.dataset.effort || '3');
        if (aEffort !== bEffort) {
          return mult * (aEffort - bEffort);
        }

        const aLift = parseFloat(a.dataset.lift || '0');
        const bLift = parseFloat(b.dataset.lift || '0');
        return mult * (bLift - aLift);
      },

      difficulty: (a, b, direction) => {
        const mult = direction === 'asc' ? 1 : -1;
        const aEffort = parseInt(a.dataset.effort || '3');
        const bEffort = parseInt(b.dataset.effort || '3');
        return mult * (aEffort - bEffort);
      },

      lift: (a, b, direction) => {
        const mult = direction === 'desc' ? 1 : -1;
        const aLift = parseFloat(a.dataset.lift || '0');
        const bLift = parseFloat(b.dataset.lift || '0');
        return mult * (bLift - aLift);
      },

      quickWin: (a, b, direction) => {
        const mult = direction === 'desc' ? 1 : -1;
        const aICE = parseFloat(a.dataset.iceScore || '0');
        const bICE = parseFloat(b.dataset.iceScore || '0');
        return mult * (bICE - aICE);
      }
    };

    return sortFns[type];
  }

  /**
   * Reapply the currently active sort
   * Called after cards are re-rendered to maintain sort order
   */
  reapplySort(): void {
    const sortType = this.activeSortType || 'confidence';
    this.activeSortType = sortType;
    this.sortCards(sortType);
  }
}
